import React, { useEffect, useState } from "react";
import apiClient from "../apiClient";
import { toast } from "react-toastify";

const PendingReceivePopup = ({ shopId }) => {
  const [pendingReceives, setPendingReceives] = useState([]);
  const [loading, setLoading]                = useState(false);
  const [visible, setVisible]                = useState(false);

  const [productDetails, setProductDetails]     = useState({});
  const [productDecisions, setProductDecisions] = useState({});
  const [processingId, setProcessingId]         = useState(null);

  const fetchPendingReceives = async () => {
    try {
      setLoading(true);
      const res  = await apiClient.post("/Liquor/GetPendingReceives", { shopId: Number(shopId) });
      const data = res.data?.data || [];
      if (data.length > 0) {
        setPendingReceives(data);
        setVisible(true);
        data.forEach(item => fetchProducts(item.receivedId));
      } else {
        setVisible(false);
      }
    } catch (err) {
      console.error("PendingReceive fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async (receivedId) => {
    try {
      const res   = await apiClient.post("/Liquor/GetStgReceivedProducts", { receivedId });
      const prods = res.data?.data || [];
      setProductDetails(prev => ({ ...prev, [receivedId]: prods }));
      const initial = {};
      prods.forEach((p, i) => {
        const pid = p.productId || p.ProductId || p.id || p.Id || i;
        initial[pid] = { status: "approved", qty: p.qty ?? p.Qty ?? 0, reason: "" };
      });
      setProductDecisions(prev => ({ ...prev, [receivedId]: initial }));
    } catch {
      setProductDetails(prev => ({ ...prev, [receivedId]: [] }));
    }
  };

  useEffect(() => {
    if (shopId) fetchPendingReceives();
  }, [shopId]);

  const setDecision = (receivedId, productId, patch) => {
    setProductDecisions(prev => ({
      ...prev,
      [receivedId]: {
        ...prev[receivedId],
        [productId]: { ...(prev[receivedId]?.[productId] || {}), ...patch },
      },
    }));
  };

  const handleSelectAll = (receivedId, prods, selectAll) => {
    const next = {};
    prods.forEach((p, i) => {
      const pid = p.productId || p.ProductId || p.id || p.Id || i;
      next[pid] = selectAll
        ? { status: "approved", qty: p.qty ?? p.Qty ?? 0, reason: "" }
        : { status: "rejected", qty: 0, reason: "" };
    });
    setProductDecisions(prev => ({ ...prev, [receivedId]: next }));
  };

  const handleSubmit = async (item) => {
    const receivedId = item.receivedId;
    const prods      = productDetails[receivedId] || [];
    const decisions  = productDecisions[receivedId] || {};
    const getDetailId = (p, i) =>
      p.detailId || p.DetailId || p.stgDetailId || p.StgDetailId || i;

    const allRejected = prods.every((p, i) => {
      const pid = p.productId || p.ProductId || p.id || p.Id || i;
      return decisions[pid]?.status === "rejected";
    });

    try {
      setProcessingId(receivedId);

      if (allRejected) {
        const reason = prods
          .map((p, i) => {
            const pid = p.productId || p.ProductId || p.id || p.Id || i;
            return decisions[pid]?.reason;
          })
          .filter(Boolean)
          .join("; ") || "Products rejected";

        await apiClient.post("/Liquor/ApproveRejectV4", {
          receivedId,
          isApproved: false,
          action: "Reject",
          rejectReason: reason,
        });
        toast.success("All products rejected — stock reverted to sender");
      } else {
        const partialItems = prods.map((p, i) => {
          const pid = p.productId || p.ProductId || p.id || p.Id || i;
          const did = getDetailId(p, i);
          const d   = decisions[pid];
          return {
            detailId: did,
            productId: pid,
            acceptedQty: d?.status === "approved" ? Number(d.qty ?? 0) : 0,
          };
        });
        await apiClient.post("/Liquor/ApproveRejectV4", {
          receivedId,
          isApproved: true,
          action: "Approve",
          rejectReason: "",
          partialItems,
        });
        const rejectedCount = partialItems.filter(x => x.acceptedQty === 0).length;
        toast.success(rejectedCount > 0
          ? `Approved with ${rejectedCount} product(s) returned to sender`
          : "Approved successfully"
        );
      }

      const updated = pendingReceives.filter(x => x.receivedId !== receivedId);
      setPendingReceives(updated);
      if (updated.length === 0) setVisible(false);
    } catch {
      toast.error("Failed to submit decision");
    } finally {
      setProcessingId(null);
    }
  };

  if (!visible) return null;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(0,0,0,0.55)", display: "flex",
      alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, width: "100%",
        maxWidth: 620, maxHeight: "90vh", display: "flex", flexDirection: "column",
        boxShadow: "0 24px 64px rgba(0,0,0,0.3)",
      }}>
        {/* Header */}
        <div style={{
          padding: "14px 18px", borderBottom: "1px solid #E5E7EB",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          background: "#3D0D26", borderRadius: "16px 16px 0 0", flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#fff" }}>Pending Approvals</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.65)", marginTop: 2 }}>
              {pendingReceives.length} transfer{pendingReceives.length !== 1 ? "s" : ""} waiting for acceptance
            </div>
          </div>
          <button onClick={() => setVisible(false)} style={{
            background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8,
            color: "#fff", width: 32, height: 32, cursor: "pointer", fontSize: 14, fontWeight: 700,
          }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px" }}>
          {pendingReceives.map(item => {
            const prods        = productDetails[item.receivedId];
            const decisions    = productDecisions[item.receivedId] || {};
            const isProcessing = processingId === item.receivedId;
            const prodList     = prods || [];

            const rejectedCount = prodList.filter((p, i) => {
              const pid = p.productId || p.ProductId || p.id || p.Id || i;
              return (decisions[pid]?.status ?? "approved") === "rejected";
            }).length;
            const approvedCount = prodList.filter((p, i) => {
              const pid = p.productId || p.ProductId || p.id || p.Id || i;
              return (decisions[pid]?.status ?? "approved") === "approved";
            }).length;
            const allChecked   = prodList.length > 0 && rejectedCount === 0;
            const someChecked  = approvedCount > 0 && rejectedCount > 0;
            const canSubmit    = prodList.length > 0;

            return (
              <div key={item.receivedId} style={{
                border: "1.5px solid #E5E7EB", borderRadius: 12, marginBottom: 12, overflow: "hidden",
              }}>
                {/* Transfer meta */}
                <div style={{ padding: "10px 14px", background: "#FDF4F8" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#7B1D4F" }}>
                    From: {item.shopName}
                  </div>
                  <div style={{ fontSize: 11, color: "#6B7280", marginTop: 3 }}>
                    Txn: {item.transactionNumber}
                    {item.remark ? ` · ${item.remark}` : ""}
                  </div>
                </div>

                {/* Products */}
                <div style={{ padding: "8px 14px" }}>
                  {!prods ? (
                    <div style={{ color: "#9CA3AF", fontSize: 12, padding: "6px 0" }}>Loading products…</div>
                  ) : prods.length === 0 ? (
                    <div style={{ color: "#9CA3AF", fontSize: 12 }}>No products found</div>
                  ) : (
                    <>
                      {/* Header row with Select All checkbox */}
                      <div style={{
                        display: "grid",
                        gridTemplateColumns: "28px 1fr 58px 48px 64px",
                        gap: "0 4px", fontSize: 9, fontWeight: 800, color: "#9CA3AF",
                        textTransform: "uppercase", letterSpacing: 0.5,
                        padding: "4px 6px", background: "#F9FAFB", borderRadius: 6, marginBottom: 4,
                        alignItems: "center",
                      }}>
                        <input
                          type="checkbox"
                          checked={allChecked}
                          ref={el => { if (el) el.indeterminate = someChecked; }}
                          onChange={e => handleSelectAll(item.receivedId, prods, e.target.checked)}
                          title="Select All"
                          style={{ width: 14, height: 14, cursor: "pointer", accentColor: "#059669" }}
                        />
                        <span>Product</span>
                        <span style={{ textAlign: "center" }}>Packing</span>
                        <span style={{ textAlign: "center" }}>Sent</span>
                        <span style={{ textAlign: "center" }}>Accept</span>
                      </div>

                      {prods.map((p, i) => {
                        const pid        = p.productId || p.ProductId || p.id || p.Id || i;
                        const sentQty    = p.qty ?? p.Qty ?? 0;
                        const d          = decisions[pid] || { status: "approved", qty: sentQty, reason: "" };
                        const isApproved = d.status === "approved";
                        const isRejected = d.status === "rejected";

                        return (
                          <div key={i}>
                            <div style={{
                              display: "grid",
                              gridTemplateColumns: "28px 1fr 58px 48px 64px",
                              gap: "0 4px", alignItems: "center",
                              padding: "6px 6px",
                              borderBottom: "1px solid #F3F4F6",
                              background: isApproved ? "#F0FDF4" : "#FFF1F1",
                              borderRadius: 4,
                            }}>
                              <input
                                type="checkbox"
                                checked={isApproved}
                                onChange={e => setDecision(item.receivedId, pid, {
                                  status: e.target.checked ? "approved" : "rejected",
                                  qty: e.target.checked ? sentQty : 0,
                                  reason: "",
                                })}
                                style={{ width: 14, height: 14, cursor: "pointer", accentColor: "#059669" }}
                              />
                              <span style={{
                                fontSize: 11, fontWeight: 600,
                                color: isApproved ? "#111827" : "#7F1D1D",
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                              }}>
                                {p.productName || p.ProductName || p.pName || p.name}
                              </span>
                              <span style={{ fontSize: 10, color: "#6B7280", textAlign: "center" }}>
                                {p.packing || p.Packing || "—"}
                              </span>
                              <span style={{ fontSize: 12, fontWeight: 700, color: "#374151", textAlign: "center" }}>
                                {sentQty}
                              </span>

                              {/* Accept qty — editable only when approved */}
                              <input
                                type="number"
                                min="0"
                                max={sentQty}
                                inputMode="numeric"
                                disabled={!isApproved}
                                value={isApproved ? d.qty : ""}
                                placeholder="—"
                                onChange={e => {
                                  const val = Math.min(Number(sentQty), Math.max(0, parseInt(e.target.value) || 0));
                                  setDecision(item.receivedId, pid, { qty: val });
                                }}
                                style={{
                                  width: "100%", padding: "3px 4px", textAlign: "center",
                                  border: `1.5px solid ${isApproved ? "#10B981" : "#E5E7EB"}`,
                                  borderRadius: 6, fontSize: 12, fontWeight: 700,
                                  color: isApproved ? "#065F46" : "#9CA3AF",
                                  background: isApproved ? "#F0FDF4" : "#F9FAFB",
                                  outline: "none", boxSizing: "border-box",
                                }}
                              />
                            </div>

                            {/* Per-product reject reason */}
                            {isRejected && (
                              <div style={{ padding: "4px 6px 6px 6px", background: "#FFF1F1" }}>
                                <input
                                  type="text"
                                  placeholder="Reason (optional)…"
                                  value={d.reason}
                                  onChange={e => setDecision(item.receivedId, pid, { reason: e.target.value })}
                                  style={{
                                    width: "100%", padding: "4px 8px",
                                    border: "1px solid #FCA5A5", borderRadius: 6,
                                    fontSize: 11, color: "#DC2626", outline: "none",
                                    boxSizing: "border-box", background: "#FFF",
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>

                {/* Submit */}
                <div style={{
                  padding: "10px 14px", borderTop: "1px solid #F3F4F6",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                }}>
                  <span style={{ fontSize: 11, color: rejectedCount > 0 ? "#DC2626" : "#10B981", fontWeight: 600 }}>
                    {rejectedCount > 0
                      ? `${rejectedCount} rejected · ${approvedCount} approved`
                      : `All ${approvedCount} product(s) approved`}
                  </span>
                  <button
                    onClick={() => handleSubmit(item)}
                    disabled={!canSubmit || isProcessing}
                    style={{
                      background: canSubmit ? "#3D0D26" : "#E5E7EB",
                      border: "none", borderRadius: 8,
                      padding: "7px 18px", fontSize: 12, fontWeight: 700,
                      cursor: canSubmit ? "pointer" : "not-allowed",
                      color: canSubmit ? "#fff" : "#9CA3AF",
                      opacity: isProcessing ? 0.6 : 1,
                    }}
                  >{isProcessing ? "Submitting…" : "Confirm Decision"}</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PendingReceivePopup;
